/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-empty */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import Uppy from '@uppy/core';
import { useUppy, Dashboard } from '@uppy/react';
import XHRUpload from '@uppy/xhr-upload';
import debounce from 'lodash/debounce';
import AwsS3 from '@uppy/aws-s3';
import Webcam from '@uppy/webcam';
import { Input, Icon } from 'rsuite';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Textarea,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Flex,
  Box,
  Text,
  Heading,
  IconButton,
  Icon as ChakraIcon,
} from '@chakra-ui/react';
import { FaImage } from 'react-icons/fa';
import { FiArrowRight, FiEdit2, FiX } from 'react-icons/fi';
import { User } from '../../../@types';
import { ModalWrapper } from '../styles/ModalWrapper';
import * as Constants from '../../../constants';
import { showNotification } from '../../notification';
import { ImageResponseWrapper } from '../../episode-video/components/QuestionResponseView';
import { ResponseModalFooter } from './ResponseModalFooter';
import {
  useSubmitResponse,
  useCurrentUser,
  useUserPromptResponses,
  getSignedUrl,
} from '../../../api';

const ExistingWrapper = styled.div`
  height: 300px;
  display: flex;
  justify-content: center;
`;

const DashboardWrapper = styled.div`
  .uppy-Dashboard-inner {
    background-color: var(--chakra-colors-gray-50);
    border-radius: 12px;
  }
`;

export const ImageResponseModal: React.FC<{
  user: User;
  promptId: string;
  show: boolean;
  questionText: string;
  icon: React.ReactNode;
  closeModal: () => void;
  setShowImageResponseModal: (show: boolean) => void;
}> = ({
  user,
  promptId,
  show,
  setShowImageResponseModal,
  questionText,
  icon,
  closeModal,
}) => {
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [fileAdded, setFileAdded] = useState(false);
  const {
    data: responses,
    isLoading: responsesLoading,
  } = useUserPromptResponses(promptId, user.id);

  const myResponse = responses && responses[Constants.RESPONSE_TYPE_IDS.IMAGE];

  const {
    mutateAsync: submitResponse,
    isLoading,
    isError,
    error: submitErrorMessage,
  } = useSubmitResponse(promptId);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const shouldSaveDescription = useRef(!myResponse);

  const [responseText, setResponseText] = useState(myResponse?.text || '');

  const handleSubmitResponse = useCallback(
    async ({ shouldShowNotification = true, ...rest }) => {
      try {
        const res = await submitResponse({
          prompt_id: promptId,
          response_type: Constants.RESPONSE_TYPE_IDS.IMAGE,
          text: responseText,
          ...rest,
        });
        if (shouldShowNotification) {
          if (res.success) {
            showNotification({
              type: 'success',
              title: 'Success!',
              message: 'Your image response was submitted!',
            });
          } else {
            showNotification({
              type: 'error',
              title: 'Oh no!',
              message:
                'There was a problem uploading your image. Please try again.',
            });
          }
        }
        return res;
      } catch (e) {
        //
      }
      return false;
    },
    [promptId, Constants, showNotification, submitResponse, responseText]
  );

  const handleClose = () => {
    setShowImageResponseModal(false);
    onClose();
  };

  useEffect(() => {
    const saveDescription = async () => {
      await handleSubmitResponse({
        shouldShowNotification: false,
      });
    };
    if (uploadSuccess && !!shouldSaveDescription.current) {
      shouldSaveDescription.current = false;
      saveDescription();
    }
  }, [uploadSuccess]);

  const uppyWebcam = useUppy(() => {
    return new (Uppy as any)({
      id: 'uppy-image',
      autoProceed: false,
      // debug: true,
      restrictions: {
        maxNumberOfFiles: 1,
        minNumberOfFiles: 1,
        allowedFileTypes: ['image/*'],
      },
      meta: {
        user_id: user.id,
        response_type: Constants.RESPONSE_TYPE_IDS.IMAGE.toString(),
      },
      onBeforeFileAdded: (currentFile: any, files: any) => {
        setFileAdded(true);
      },
    })
      .use(AwsS3, {
        async getUploadParameters(file: any) {
          const {
            url,
            base_path,
            full_path,
            base_name,
            full_name,
          } = await getSignedUrl(promptId, file.extension);

          uppyWebcam.setMeta({
            base_path,
            full_path,
            base_name,
            full_name,
            file_type: file.type,
          });
          return {
            url,
            method: 'PUT',
            headers: {
              contentType: file.type,
            },
          };
        },
      })
      .on('upload-success', async (file: any, response: any) => {
        setFileAdded(false);
        const {
          base_path,
          full_path,
          base_name,
          full_name,
          file_type,
        } = file.meta;
        const submit = await handleSubmitResponse({
          base_path,
          full_path,
          base_name,
          full_name,
          file_type,
        });
        setUploadSuccess(submit.success);
      });
  });

  useEffect(() => {
    if (show) onOpen();
  }, [show]);

  if (!user || responsesLoading) return null;

  const { message: errorMessage = '' } = (submitErrorMessage as any) || {};
  const strippedText = questionText.replace(/<[^>]+>/g, '');

  return (
    <Modal size="3xl" isOpen={isOpen} onClose={handleClose}>
      <ModalOverlay />
      <ModalWrapper>
        <ModalContent className="image-response">
          <ModalHeader
            p={6}
            display="flex"
            justifyContent="space-between"
            gap={4}
          >
            <Flex alignItems="center" gap={2}>
              <Flex
                bg="newGreen.500"
                w={8}
                h={8}
                borderRadius="full"
                justifyContent="center"
                alignItems="center"
              >
                <ChakraIcon as={FaImage} fontSize={14} color="white" />
              </Flex>
              <Text fontWeight={700} fontSize={16}>
                Respond with an image
              </Text>
            </Flex>
            <IconButton
              aria-label="Close"
              icon={<ChakraIcon as={FiX} fontSize={24} />}
              colorScheme="darkBlue"
              onClick={onClose}
              borderRadius="full"
              size="sm"
            />
          </ModalHeader>
          <ModalBody pb={6}>
            <FormControl isInvalid={isError}>
              <FormLabel>
                <Text fontWeight={600} fontSize={14} textAlign="center" w="90%">
                  <div
                    className="question-text"
                    dangerouslySetInnerHTML={{ __html: strippedText }}
                  />
                </Text>
              </FormLabel>
              <Flex gap={2} alignItems="center">
                <FaImage fontSize={20} />
                <Text fontSize={14} fontWeight={600}>
                  {!myResponse ? 'Upload an Image' : 'Your Image Response'}
                </Text>
              </Flex>

              <Flex textAlign="center" align="center">
                <Box w="100%">
                  {uppyWebcam && !myResponse && (
                    <DashboardWrapper>
                      <Dashboard
                        uppy={uppyWebcam}
                        proudlyDisplayPoweredByUppy={false}
                      />
                    </DashboardWrapper>
                  )}
                  {!!myResponse && (
                    <ExistingWrapper>
                      <ImageResponseWrapper response={myResponse} isEditView />
                    </ExistingWrapper>
                  )}
                </Box>
              </Flex>

              <FormLabel
                fontSize={14}
                fontWeight={600}
                display="flex"
                gap={2}
                mt={4}
              >
                <FiEdit2 fontSize={20} />
                Description:
              </FormLabel>
              <Textarea
                placeholder="Enter a description of your image... (optional)"
                size="md"
                Textarea
                bg="gray.50"
                borderRadius={12}
                onChange={(e) => setResponseText(e.target.value)}
                value={responseText}
              />
              <FormErrorMessage>{`${errorMessage}`}</FormErrorMessage>
            </FormControl>
          </ModalBody>

          <ResponseModalFooter
            myResponse={myResponse}
            handleClose={handleClose}
            fileAdded={fileAdded}
            isLoading={isLoading}
            isDisabled={!myResponse && !uploadSuccess}
            onSubmit={handleSubmitResponse}
            onDelete={() => null}
          />
        </ModalContent>
      </ModalWrapper>
    </Modal>
  );
};
